import { useEffect, useState } from "react";
import clsx from "clsx";
import { useLocale } from "@/i18n";
import { ClockIcon } from "@/components/ui/Icons";

function formatRemaining(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function BookingHoldTimer({
  expiresAt,
  onExpired,
  className,
}: {
  expiresAt: string;
  onExpired: () => void;
  className?: string;
}) {
  const { messages: m } = useLocale();
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
    return Math.max(0, diff);
  });

  useEffect(() => {
    const tick = () => {
      const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
      const next = Math.max(0, diff);
      setRemainingSeconds(next);
      if (next <= 0) {
        onExpired();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, onExpired]);

  const urgent = remainingSeconds <= 60;

  return (
    <div
      className={clsx(
        "flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm",
        urgent
          ? "border-amber-300 bg-amber-50 text-amber-950"
          : "border-brand-100 bg-brand-50/70 text-brand-900",
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <span
        className={clsx(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          urgent ? "bg-amber-100 text-amber-700" : "bg-white text-brand-600",
        )}
      >
        <ClockIcon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="font-semibold">
          {m.holdTimer.label}{" "}
          <span className="tabular-nums">{formatRemaining(remainingSeconds)} Min.</span>
        </p>
        <p className="text-xs opacity-75">{m.holdTimer.hint}</p>
      </div>
    </div>
  );
}
